import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router';
import { classifyLinkViewError, ShareLinkViewErrorScreen, type ShareLinkViewErrorKind } from '@indipia/shared-features';
import { photoApi, type BundleShareViewData } from '../api/photoApi';
import { ShareLinkMain } from '../main/ShareLinkMain';

type Phase = 'loading' | 'error' | 'content';

function LoadingView() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--alpha-white-600)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ animation: 'spin 1s linear infinite' }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
      <p style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--alpha-white-600)' }}>링크를 불러오는 중…</p>
    </div>
  );
}

export function PhotoAlbumShareViewPage() {
  const [searchParams] = useSearchParams();
  const { pathname, hash } = useLocation();
  const shareId = searchParams.get('shareId');
  const rawHistoryId = searchParams.get('historyId');
  const historyId = rawHistoryId !== null && /^\d+$/.test(rawHistoryId) ? Number(rawHistoryId) : undefined;
  const isPreview = pathname.endsWith('/PREVIEW');
  // 미리보기는 ShareGate를 안 거쳐서 로그인 토큰이 필요 — 앱이 실어 보낸다.
  // 쿼리파라미터가 아니라 프래그먼트(#)로 온다 — #뒤는 브라우저가 서버로 전송하지
  // 않아 접근 로그에 토큰 평문이 남지 않는다(C-05 §7.1 마스킹 대상 정합).
  const previewToken = useMemo(
    () => (hash ? new URLSearchParams(hash.slice(1)).get('previewToken') ?? undefined : undefined),
    [hash],
  );

  const [phase, setPhase] = useState<Phase>('loading');
  const [data, setData] = useState<BundleShareViewData | null>(null);
  const [errorKind, setErrorKind] = useState<ShareLinkViewErrorKind>('TEMPORARY');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!shareId || isNaN(Number(shareId))) {
      setErrorKind('INVALID');
      setPhase('error');
      return;
    }
    setPhase('loading');
    void (async () => {
      try {
        const res = await photoApi.getBundleViewByShareId(Number(shareId), isPreview, previewToken, historyId);
        setData(res.data.data);
        setPhase('content');
      } catch (e) {
        // 만료·회수·무효·일시 오류 4종(+접속 시간 초과) — 스케줄 열람 화면과 같은 분류 기준
        setErrorKind(classifyLinkViewError(e));
        setPhase('error');
      }
    })();
  }, [shareId, isPreview, previewToken, historyId, reloadKey]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
      {isPreview && (
        <div style={{ flexShrink: 0, background: 'var(--gt-primary)', padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
          </svg>
          <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gray-0)' }}>미리보기</span>
        </div>
      )}
      <ShareLinkMain data={phase === 'content' ? (data ?? undefined) : undefined} isPreview={isPreview} previewToken={previewToken} shareId={shareId ? Number(shareId) : null} />
      {phase === 'error' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10 }}>
          {isPreview && errorKind === 'SESSION' ? (
            <ShareLinkViewErrorScreen
              kind="SESSION"
              title="로그인이 필요합니다"
              description="발송자 본인 계정으로 앱에 로그인한 뒤 다시 시도해 주세요."
            />
          ) : (
            <ShareLinkViewErrorScreen kind={errorKind} onRetry={() => setReloadKey((k) => k + 1)} />
          )}
        </div>
      )}
      {phase === 'loading' && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--gray-900)', zIndex: 10,
          display: 'flex', flexDirection: 'column',
          fontFamily: 'var(--font-family)',
        }}>
          <div style={{ padding: '18px 28px', display: 'flex', alignItems: 'center' }}>
            <img src="/brand/indipia-logo-dark.svg" alt="INDIPIA" style={{ height: 24, flexShrink: 0 }} />
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
            <LoadingView />
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--alpha-white-300)' }}>Powered by INDIPIA</p>
          </div>
        </div>
      )}
    </div>
  );
}
